import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { FP } from "./tokens";
import { fpInputSx } from "./inputStyles";

/**
 * Diálogo de confirmación estándar. Para acciones destructivas (anular,
 * eliminar, cancelar) pase `destructivo` (botón rojo) y, si el backend
 * registra el motivo, `razonObligatoria`: el botón queda deshabilitado
 * hasta que se escriba una razón, que llega como argumento de onConfirmar.
 *
 *   <ConfirmarDialog abierto={open} titulo="Eliminar gasto" destructivo
 *     mensaje="¿Seguro que desea eliminar este gasto?"
 *     textoConfirmar="Eliminar" onCerrar={...} onConfirmar={(razon) => ...} />
 */
export default function ConfirmarDialog({
  abierto,
  titulo,
  mensaje,
  children,
  textoConfirmar = "Confirmar",
  // "Cancelar" se lee mal cuando la acción confirmada también se llama
  // cancelar (cancelar un préstamo): ahí conviene "Volver".
  textoCerrar = "Cancelar",
  destructivo = false,
  razonObligatoria = false,
  etiquetaRazon = "Razón (obligatoria)",
  // Para diálogos que exigen una decisión propia en `children` antes de poder
  // confirmar (p. ej. qué hacer con el chasis al cancelar un préstamo).
  confirmarDeshabilitado = false,
  cargando = false,
  onCerrar,
  onConfirmar,
}) {
  const [razon, setRazon] = useState("");

  useEffect(() => {
    if (abierto) setRazon("");
  }, [abierto]);

  const deshabilitado = cargando || confirmarDeshabilitado || (razonObligatoria && !razon.trim());

  return (
    <Dialog
      open={abierto}
      onClose={cargando ? undefined : onCerrar}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, boxShadow: FP.shadow.md } }}
    >
      <DialogTitle sx={{ fontWeight: 800, color: FP.color.textPrimary }}>{titulo}</DialogTitle>
      <DialogContent>
        {mensaje ? (
          <DialogContentText sx={{ color: FP.color.textSecondary }}>{mensaje}</DialogContentText>
        ) : null}
        {children}
        {razonObligatoria ? (
          <TextField
            label={etiquetaRazon}
            value={razon}
            onChange={(e) => setRazon(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            autoFocus
            InputLabelProps={{ shrink: true }}
            sx={{ ...fpInputSx, mt: 2 }}
          />
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onCerrar}
          disabled={cargando}
          sx={{ textTransform: "none", color: FP.color.textSecondary, fontWeight: 700 }}
        >
          {textoCerrar}
        </Button>
        <Button
          variant="contained"
          disabled={deshabilitado}
          onClick={() => onConfirmar(razon.trim())}
          sx={{
            bgcolor: destructivo ? FP.color.danger : FP.color.navy,
            color: "#FFFFFF",
            borderRadius: 1.4,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "none",
            "&:hover": { bgcolor: destructivo ? "#BE123C" : FP.color.navyDeep, boxShadow: "none" },
          }}
        >
          {cargando ? "Procesando…" : textoConfirmar}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
